/**
 * zod 스키마 — 저장·전송 경계의 유일한 검증 지점.
 *
 * 여기서 막지 못한 값은 해석 엔진에 도달한다. 해석 엔진은 전역 함수(예외를 던지지 않음)라
 * 잘못된 값을 조용히 흘려보내므로, 잘못은 반드시 **여기서** 걸려야 한다(R8).
 */
import { z } from 'zod';
import { HHMM_PATTERN, isValidTimeZone, parseHhmm } from './time.js';
import { TRANSITIONS } from './types.js';
import { HTML_FITS, HTML_SIZE_MAX, HTML_SIZE_MIN } from './html-fit.js';
import { TEMPLATE_CAPABILITIES } from './templates/schema.js';
/**
 * 하트비트 `content_types` 에 올 수 있는 이름 — 모르는 이름을 거를 때 쓴다. 콘텐츠 종류와, 처음 다섯 밖의 템플릿을 그릴 줄
 * 안다는 표시(`template:split`, `templateCapability`).
 */
const CONTENT_TYPE_NAMES = ['image', 'video', 'web', 'template', 'html', ...TEMPLATE_CAPABILITIES];
/** `HH:MM` (00:00 ~ 24:00). 형식과 의미를 모두 검증한다. */
export const hhmmSchema = z
    .string()
    .regex(HHMM_PATTERN, 'HH:MM 형식이어야 합니다 (00:00 ~ 24:00)')
    .refine((v) => parseHhmm(v) !== null, '유효하지 않은 시각입니다');
/** IANA 타임존 식별자. */
export const timezoneSchema = z
    .string()
    .min(1)
    .refine(isValidTimeZone, '알 수 없는 타임존입니다 (IANA 식별자 필요)');
/** 요일 집합 — 0(일) ~ 6(토), 중복 없음, 최소 1개. */
export const daysSchema = z
    .array(z.number().int().min(0).max(6))
    .min(1, '요일을 최소 하나 선택해야 합니다')
    .max(7)
    .refine((d) => new Set(d).size === d.length, '요일이 중복되었습니다');
/**
 * 시간대 구간.
 *
 * `start === end` 는 거부한다. 의미가 "0분"인지 "24시간"인지 사용자가 의도한 바를
 * 알 수 없기 때문이다. 24시간을 원하면 `00:00 ~ 24:00` 으로 명시하게 한다.
 * `end < start` 는 **정상**이며 자정을 넘는 심야 구간을 뜻한다(R1).
 */
export const daypartSchema = z
    .object({
    days: daysSchema,
    start: hhmmSchema,
    end: hhmmSchema,
})
    .refine((d) => d.start !== d.end, {
    message: '시작과 종료가 같습니다. 종일 편성은 00:00 ~ 24:00 으로 지정하세요',
    path: ['end'],
});
export const transitionSchema = z.enum(TRANSITIONS);
export const assetTypeSchema = z.enum(['image', 'video', 'web', 'html']);
export const orientationSchema = z.enum(['landscape', 'portrait']);
/**
 * 화면을 몇 도 돌려 그릴 것인가.
 *
 * `orientation` 2값으로는 **어느 쪽으로 돌려 세웠는지**를 말할 수 없다. 세로 설치의
 * 절반은 시계방향, 절반은 반시계방향이고 그 둘은 위아래가 뒤집힌다. 180°(천장·상단
 * 매립)도 표현할 곳이 없었다. 그래서 각도를 따로 둔다.
 *
 * `orientation` 은 남겨 둔다 — 옛 화면·옛 콘솔이 계속 읽고, 서버가 이 값에서 파생시킨다.
 */
export const rotationSchema = z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]);
export const assignmentKindSchema = z.enum(['none', 'playlist', 'schedule']);
export const campaignModeSchema = z.enum(['replace', 'blend']);
export const targetKindSchema = z.enum(['all', 'tags', 'groups', 'displays']);
export const objectIdSchema = z.string().regex(/^[0-9a-f]{24}$/i, '유효한 id 가 아닙니다');
const tagSchema = z.string().trim().min(1).max(40);
/**
 * 배정 — kind 에 따라 필요한 참조가 실제로 있는지까지 본다.
 * "kind 는 schedule 인데 schedule_id 가 null" 같은 상태를 저장 자체에서 막는다.
 */
export const assignmentSchema = z
    .object({
    kind: assignmentKindSchema,
    playlist_id: objectIdSchema.nullable().default(null),
    schedule_id: objectIdSchema.nullable().default(null),
})
    .superRefine((v, ctx) => {
    if (v.kind === 'playlist' && !v.playlist_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['playlist_id'], message: '플레이리스트를 선택하세요' });
    }
    if (v.kind === 'schedule' && !v.schedule_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['schedule_id'], message: '스케줄을 선택하세요' });
    }
});
export const targetSchema = z
    .object({
    kind: targetKindSchema,
    tags: z.array(tagSchema).max(50).default([]),
    group_ids: z.array(objectIdSchema).max(200).default([]),
    display_ids: z.array(objectIdSchema).max(2000).default([]),
})
    .superRefine((v, ctx) => {
    const empty = (v.kind === 'tags' && v.tags.length === 0) ||
        (v.kind === 'groups' && v.group_ids.length === 0) ||
        (v.kind === 'displays' && v.display_ids.length === 0);
    if (empty) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['kind'], message: '타겟이 비어 있어 어떤 화면에도 적용되지 않습니다' });
    }
});
// ─────────────────────────────────────────────────────────────────────────────
// 쓰기 입력 스키마
// ─────────────────────────────────────────────────────────────────────────────
export const assetCreateSchema = z.object({
    title: z.string().trim().min(1).max(200),
    type: assetTypeSchema,
    storage_key: z.string().min(1).max(512),
    mime: z.string().min(1).max(128),
    size: z.number().int().min(0),
    checksum: z.string().regex(/^[0-9a-f]{64}$/, 'sha256 16진 64자여야 합니다'),
    width: z.number().int().positive().nullable().default(null),
    height: z.number().int().positive().nullable().default(null),
    duration_sec: z.number().positive().max(24 * 3600).nullable().default(null),
    default_dwell_sec: z.number().int().min(1).max(3600).default(8),
    tags: z.array(tagSchema).max(30).default([]),
});
/**
 * 게시 기간 — 시작·종료(절대 시각, null 이면 그쪽 끝 제한 없음). 종료는 시작보다 뒤여야 한다.
 *
 * 둘을 함께 검사하려면 둘 다 들어와야 한다. 한쪽만 고치는 PATCH 는 서버가 저장된 다른 쪽과 견준다.
 */
export const assetValiditySchema = {
    valid_from: z.coerce.date().nullable().optional(),
    valid_until: z.coerce.date().nullable().optional(),
};
const validityOrder = (value) => !value.valid_from || !value.valid_until || value.valid_from.getTime() < value.valid_until.getTime();
const VALIDITY_ORDER_MESSAGE = { message: '게시 종료는 시작보다 뒤여야 합니다', path: ['valid_until'] };
export const assetPatchSchema = z
    .object({
    title: z.string().trim().min(1).max(200).optional(),
    default_dwell_sec: z.number().int().min(1).max(3600).optional(),
    tags: z.array(tagSchema).max(30).optional(),
    archived: z.boolean().optional(),
    /** 웹 콘텐츠만 바꿀 수 있다. 다른 종류에 주면 서버가 거부한다. */
    source_url: z.string().url().optional(),
    /** 소리 크기(0~100) — 영상만. 0 이면 소리 없이 튼다. 화면의 음량과 곱해진다. */
    volume: z.number().int().min(0).max(100).optional(),
    /** 화면 맞춤 — 올린 HTML 만(2026-10-20, `html-fit.ts`). 맞춤(auto)·채움(cover)·그대로(none). */
    html_fit: z.enum(HTML_FITS).optional(),
    /** 기준 크기 — 올린 HTML 만. 콘솔이 재서(또는 사람이 정해) 적는다. null 이면 늘고 주는 HTML(그대로 띄운다). */
    html_size: z
        .object({
        width: z.number().int().min(HTML_SIZE_MIN).max(HTML_SIZE_MAX),
        height: z.number().int().min(HTML_SIZE_MIN).max(HTML_SIZE_MAX),
    })
        .nullable()
        .optional(),
    /** 기준 크기를 사람이 정했는가 — 그러면 콘솔이 저절로 다시 재서 덮어쓰지 않는다(`html_size` 와 함께 보낸다). */
    html_size_manual: z.boolean().optional(),
    /**
     * 적용할 화면 — 올린 HTML 만(2026-10-20). 콘솔이 이 화면의 크기로 미리 보이고 맞춤을 고르게 하는 기준 화면이다(재생에는
     * 들지 않는다). null 이면 정하지 않았다.
     */
    html_target_display_id: objectIdSchema.nullable().optional(),
    ...assetValiditySchema,
})
    .refine(validityOrder, VALIDITY_ORDER_MESSAGE);
/** 여러 콘텐츠의 게시 기간을 한 번에 — 캠페인 소재 묶음을 같은 계약 기간으로. */
export const assetValidityBulkSchema = z
    .object({
    asset_ids: z.array(objectIdSchema).min(1).max(200),
    valid_from: z.coerce.date().nullable(),
    valid_until: z.coerce.date().nullable(),
})
    .refine(validityOrder, VALIDITY_ORDER_MESSAGE);
export const playlistItemSchema = z.object({
    asset_id: objectIdSchema,
    dwell_sec: z.number().int().min(1).max(3600).default(8),
    transition: transitionSchema.default('fade'),
    enabled: z.boolean().default(true),
});
export const playlistCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).default(''),
    default_transition: transitionSchema.default('fade'),
    items: z.array(playlistItemSchema).max(500).default([]),
});
export const playlistPatchSchema = playlistCreateSchema.partial().extend({
    archived: z.boolean().optional(),
});
/**
 * 규칙 하나가 무엇을 트나.
 *
 * **플레이리스트 또는 콘텐츠 하나** — 정확히 한쪽이다. 콘텐츠를 직접 가리킬 수 있게
 * 한 이유는 현장 요청의 절반이 "이 시간에는 이 영상 하나"이기 때문이다. 그때마다
 * 한 항목짜리 플레이리스트를 만들게 하면 플레이리스트 목록이 일회용으로 가득 차고,
 * 나중에 어느 것이 진짜 편성인지 아무도 구분하지 못한다.
 *
 * 둘 다 비거나 둘 다 채워지는 상태를 **저장 자체에서 막는다**(`assignment` 와 같은
 * 방식이다). 그런 규칙은 해석 단계에서 조용히 아무것도 틀지 않는데, 그 침묵은
 * 화면 앞에서만 보인다.
 */
export const scheduleRuleSchema = z
    .object({
    id: z.string().trim().min(1).max(64),
    label: z.string().max(120).default(''),
    days: daysSchema,
    start: hhmmSchema,
    end: hhmmSchema,
    playlist_id: objectIdSchema.nullable().default(null),
    asset_id: objectIdSchema.nullable().default(null),
    priority: z.number().int().min(0).max(10_000).default(0),
})
    .superRefine((v, ctx) => {
    if (v.playlist_id && v.asset_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['asset_id'],
            message: '플레이리스트와 콘텐츠 중 하나만 지정하세요',
        });
    }
    if (!v.playlist_id && !v.asset_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['playlist_id'],
            message: '플레이리스트나 콘텐츠를 지정하세요',
        });
    }
});
export const scheduleCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    timezone: timezoneSchema.default('Asia/Seoul'),
    default_playlist_id: objectIdSchema.nullable().default(null),
    /** 평소 편성을 콘텐츠 하나로(2026-10-21) — `default_playlist_id` 와 함께 채울 수 없다. */
    default_asset_id: objectIdSchema.nullable().default(null),
    rules: z.array(scheduleRuleSchema).max(200).default([]),
})
    .superRefine((v, ctx) => {
    if (v.default_playlist_id && v.default_asset_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default_asset_id'],
            message: '평소 편성은 플레이리스트와 콘텐츠 중 하나만 지정하세요',
        });
    }
    const ids = v.rules.map((r) => r.id);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rules'], message: '규칙 id 가 중복되었습니다' });
    }
    v.rules.forEach((r, i) => {
        if (r.start === r.end) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['rules', i, 'end'],
                message: '시작과 종료가 같습니다. 종일은 00:00 ~ 24:00 으로 지정하세요',
            });
        }
    });
});
/**
 * 스케줄 고치기. 평소 편성은 둘 중 하나다 — 한쪽을 채워 보내면 서버가 다른 쪽을 비운다(옛 콘솔은
 * `default_playlist_id` 만 보낸다). 둘 다 채워 보내는 것만 막는다.
 */
export const schedulePatchSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    timezone: timezoneSchema.optional(),
    default_playlist_id: objectIdSchema.nullable().optional(),
    default_asset_id: objectIdSchema.nullable().optional(),
    rules: z.array(scheduleRuleSchema).max(200).optional(),
})
    .superRefine((v, ctx) => {
    if (v.default_playlist_id && v.default_asset_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default_asset_id'],
            message: '평소 편성은 플레이리스트와 콘텐츠 중 하나만 지정하세요',
        });
    }
});
/**
 * 화면의 운영 시간 — 고객사 환경 설정의 운영 시간을 이어받을지(`inherit`), 늘 켤지(`always`),
 * 이 화면만 따로 정할지(`custom`).
 *
 * 사옥마다 문 여는 시간이 다르고, 24시간 도는 자리(보안 데스크 옆 공지판)도 있다. 고객사 하나에
 * 운영 시간 하나로는 모자라 화면이 덮을 수 있게 한다.
 */
export const displayHoursSchema = z
    .object({
    mode: z.enum(['inherit', 'always', 'custom']),
    rules: z.array(daypartSchema).max(14).default([]),
})
    .refine((value) => value.mode !== 'custom' || value.rules.length > 0, {
    message: '이 화면만 따로 정하려면 운영 시간 규칙이 하나 이상 있어야 합니다',
    path: ['rules'],
});
/**
 * 화면의 소리(2026-10-19) — 음량(0~100)과 음소거. 영상의 소리에 콘텐츠의 소리 크기와 곱해 쓴다. 기본은 100·켜짐
 * (예전처럼 늘 최대). 사무실 로비처럼 소리가 나면 안 되는 자리는 음소거로.
 */
export const displayAudioSchema = z.object({
    volume: z.number().int().min(0).max(100),
    muted: z.boolean(),
});
/**
 * 에이전트 설치 시간대(2026-10-19 기능 점검 G14) — 무인 업데이트를 이 시간대에만 깐다. 화면이 선 곳의 시각(화면 타임존)이다.
 *
 * 예전에는 승격하는 순간 모든 화면이 곧바로 받아 재시작했다 — 낮에 올리면 영업 중인 매장의 화면이 깜빡였다.
 * `end < start` 는 자정을 넘는 구간(23:00~05:00)이다. 같으면 거절한다(0분인지 하루 종일인지 모른다 — 하루 종일은 해제).
 * 정비 화면의 [지금 업데이트] 는 시간대와 상관없이 깐다 — 사람이 앞에 있다.
 */
export const installWindowSchema = z
    .object({ start: hhmmSchema, end: hhmmSchema })
    // 24:00 은 00:00 과 같은 자리다.
    .refine((w) => (parseHhmm(w.start) ?? -1) % 1440 !== (parseHhmm(w.end) ?? -2) % 1440, {
    message: '시작과 끝이 같습니다. 언제든 깔게 하려면 설치 시간대를 해제하세요',
    path: ['end'],
});
export const displayPatchSchema = z.object({
    name: z.string().trim().max(200).optional(),
    /** 목록에서 감춘다. 삭제와 달리 되돌릴 수 있고 재생에는 영향이 없다. */
    hidden: z.boolean().optional(),
    location: z.string().max(200).optional(),
    region: z.string().max(80).optional(),
    timezone: timezoneSchema.nullable().optional(),
    tags: z.array(tagSchema).max(40).optional(),
    orientation: orientationSchema.optional(),
    rotation: rotationSchema.optional(),
    group_id: objectIdSchema.nullable().optional(),
    assignment: assignmentSchema.optional(),
    channel: z.enum(['stable', 'canary']).optional(),
    pinned_version: z.string().max(40).nullable().optional(),
    hours: displayHoursSchema.optional(),
    audio: displayAudioSchema.optional(),
});
export const campaignCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    playlist_id: objectIdSchema,
    target: targetSchema,
    start_at: z.coerce.date().nullable().default(null),
    end_at: z.coerce.date().nullable().default(null),
    dayparts: z.array(daypartSchema).max(20).default([]),
    timezone: timezoneSchema.nullable().default(null),
    priority: z.number().int().min(0).max(10_000).default(100),
    mode: campaignModeSchema.default('replace'),
    blend_every: z.number().int().min(1).max(100).default(3),
    enabled: z.boolean().default(true),
})
    .refine((v) => !v.start_at || !v.end_at || v.start_at <= v.end_at, {
    message: '종료가 시작보다 빠릅니다',
    path: ['end_at'],
});
export const campaignPatchSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    playlist_id: objectIdSchema.optional(),
    target: targetSchema.optional(),
    start_at: z.coerce.date().nullable().optional(),
    end_at: z.coerce.date().nullable().optional(),
    dayparts: z.array(daypartSchema).max(20).optional(),
    timezone: timezoneSchema.nullable().optional(),
    priority: z.number().int().min(0).max(10_000).optional(),
    mode: campaignModeSchema.optional(),
    blend_every: z.number().int().min(1).max(100).optional(),
    enabled: z.boolean().optional(),
});
export const groupCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    assignment: assignmentSchema.optional(),
});
/**
 * 저장공간 값의 상한 — 1 PiB.
 *
 * 한도가 필요한 이유는 정확성이 아니라 **말이 안 되는 값을 걸러 내기** 위해서다. 셸이
 * 실패해 -1 이나 엉뚱한 큰 수를 돌려주면 콘솔에 "여유 8 EB" 가 뜨고, 그걸 본 사람은
 * 이 화면의 다른 숫자도 믿지 않게 된다.
 */
const STORAGE_BYTES_MAX = 1024 ** 5;
/**
 * 콘솔이 화면에 거는 명령. 재부팅은 0.7.6, 앱 종료·전원 종료는 0.7.7, 정비 모드 해제는 0.7.17 부터 안다.
 * 앱 종료·전원 종료는 원격으로 되돌릴 수 없다 — 현장에서 다시 켜야 한다.
 *
 * **이름을 더할 때는 옛 서버를 생각한다.** 0.7.17 전의 서버는 하트비트의 `commands` 에 모르는 이름이 있으면
 * 하트비트를 통째로 거절했다(화면이 오프라인으로 보인다). 그래서 서버가 편성 응답에 아는 이름을 싣고
 * (`agent.command_kinds`), 화면은 그 안에서만 알린다 — 싣지 않은 옛 서버에는 앞의 셋만.
 */
export const AGENT_COMMAND_KINDS = ['reboot', 'quit', 'power_off', 'exit_maintenance'];
const isAgentCommandKind = (kind) => AGENT_COMMAND_KINDS.includes(kind);
/**
 * 기기 상태(2026-10-19 기능 점검 G10) — 하트비트에 실어 콘솔 화면 상세의 [기기 상태] 에 그린다. 값마다 모르면 빠지거나
 * null 이다(셸·OS 마다 읽을 수 있는 것이 다르다 — 안드로이드 8 이상은 앱이 기기 전체 CPU 를 읽지 못한다). **값이 이상하면
 * 하트비트를 거절하지 않고 그 칸만 버린다** — 상태 한 칸 때문에 화면이 오프라인으로 보이면 안 된다.
 */
const lenient = (schema) => schema.nullable().optional().catch(undefined);
export const deviceHealthSchema = z
    .object({
    /** 기기 전체 CPU 사용률(%). */
    cpu_percent: lenient(z.number().min(0).max(100)),
    mem_total: lenient(z.number().int().min(0).max(STORAGE_BYTES_MAX)),
    mem_available: lenient(z.number().int().min(0).max(STORAGE_BYTES_MAX)),
    /** 기기에서 가장 뜨거운 곳(°C). */
    temperature_c: lenient(z.number().min(-40).max(150)),
    /** 안드로이드 10+ 의 발열 단계 — none·light·moderate·severe·critical·emergency·shutdown. */
    thermal_status: lenient(z.string().max(20)),
    /** 기기가 켜진 지(초). */
    uptime_sec: lenient(z.number().int().min(0).max(10 * 365 * 86_400)),
    /** 재생 앱이 뜬 지(초). */
    app_uptime_sec: lenient(z.number().int().min(0).max(10 * 365 * 86_400)),
    model: lenient(z.string().max(120)),
    os: lenient(z.string().max(120)),
    /** 망 — 유선·Wi-Fi, Wi-Fi 이름·신호(dBm)·대역(MHz), IP. 셸이 읽은 것. */
    network: lenient(z.object({
        kind: z.string().max(20),
        connected: z.boolean(),
        ssid: z.string().max(64).nullable().optional(),
        wifi_rssi: z.number().int().min(-126).max(0).nullable().optional(),
        wifi_mhz: z.number().int().min(0).max(100_000).nullable().optional(),
        ipv4: z.string().max(45).nullable().optional(),
    })),
})
    .partial();
export const heartbeatSchema = z.object({
    current_asset_id: objectIdSchema.nullable().optional(),
    current_playlist_id: objectIdSchema.nullable().optional(),
    resolved_revision: z.string().max(200).optional(),
    app_version: z.string().max(40).optional(),
    screen_w: z.number().int().positive().max(30_000).optional(),
    screen_h: z.number().int().positive().max(30_000).optional(),
    /** 플레이어가 자체 판단한 상태 - 운영 대시보드의 이상 탐지에 쓴다. */
    cache_bytes: z.number().int().min(0).optional(),
    /**
     * 이 기기가 미디어를 **더 둘 수 있는 여유.**
     *
     * `cache_bytes` 는 "얼마나 썼나" 만 말한다. 현장에서 정작 묻는 것은 "이 영상을 더 보낼 수
     * 있나" 인데, 그 답은 쓴 양이 아니라 남은 양에 있다. 공간이 바닥나면 웹뷰가 받아 둔 파일을
     * 임의로 버리기 시작해 **재생이 조용히 구멍 난다** — 그때는 이미 늦다.
     *
     * **`source` 가 이 수의 뜻을 가른다.** 네이티브 셸은 파티션의 실제 여유를 읽지만(`disk`),
     * 셸이 없는 브라우저 키오스크는 브라우저가 이 오리진에 허락한 할당량밖에 모른다(`quota`).
     * 뒤쪽은 상황에 따라 줄었다 늘고 디스크 전체와도 다르므로, 콘솔이 출처를 함께 적어야
     * 사람이 그 수를 어디까지 믿을지 안다.
     *
     * 옛 에이전트는 이 값을 보내지 않는다 — 그때는 **0 이 아니라 "모른다"** 다. 둘을 섞으면
     * 멀쩡한 화면이 "공간 없음" 으로 보인다.
     */
    storage: z
        .object({
        free_bytes: z.number().int().min(0).max(STORAGE_BYTES_MAX),
        total_bytes: z.number().int().min(0).max(STORAGE_BYTES_MAX),
        source: z.enum(['disk', 'quota']),
    })
        .optional(),
    /**
     * 이 기기가 서버로 나가는 인터페이스의 MAC(대문자 콜론 꼴, `A4:5E:60:C1:02:3F`).
     *
     * 현장에서 공유기 DHCP 예약·방화벽 허용을 잡을 때 묻는 값이다. **모르면 싣지 않는다** — 안드로이드 11+ 는
     * 일반 앱에 MAC 을 주지 않고(기기 소유자면 Wi-Fi MAC 은 읽힌다), 브라우저는 아예 모른다. 서버는 마지막으로
     * 받은 값을 지우지 않는다.
     */
    mac: z
        .string()
        .regex(/^[0-9A-Fa-f]{2}(:[0-9A-Fa-f]{2}){5}$/, 'MAC 은 AA:BB:CC:DD:EE:FF 꼴입니다')
        .optional(),
    last_error: z.string().max(500).nullable().optional(),
    /**
     * 현장 기술자가 정비 모드로 화면을 빠져나가 있는가.
     *
     * 정비 중에는 재생과 증빙이 함께 멈춘다. 이 값이 없으면 운영은 그 상태를
     * "온라인인데 증빙이 없다"(알람 4번)로 읽어 장애로 취급한다. 정상 작업이
     * 알람을 울리기 시작하면 진짜 장애가 왔을 때 아무도 보지 않게 된다.
     */
    maintenance: z.boolean().optional(),
    /**
     * 업데이트가 지금 어느 단계인가 — **실패를 기기 안에 가두지 않기 위한 것이다.**
     *
     * 화면은 이미 정확히 알고 있었다 — 물어보고, 내려받고, 설치에서 멈췄다는 것까지.
     * 그런데 그 사실이 기기 밖으로 나오지 않아, 운영에게는 "버전이 안 올라간다" 로만 보였다.
     * 왜 안 올라가는지 — 묻지를 않는 것인지, 받다 실패하는 것인지, **누군가 화면 앞에서
     * 눌러야 끝나는 것인지**는 서로 다른 조치가 필요한데 구별할 길이 없었다.
     *
     * 옆에 사람이 없는 화면이라 더 그렇다 — 화면에 띄워 놓는 것은 아무것도 알리지 않는 것과 같다.
     */
    update: z
        .object({
        phase: z.enum([
            'idle',
            'checking',
            'current',
            'available',
            'installing',
            'needs-confirm',
            'failed',
            'unsupported',
        ]),
        /** 받으려는 버전. 단계에 따라 없을 수 있다. */
        version: z.string().max(40).nullable().optional(),
        /** 사람이 읽고 다음 수를 정할 수 있는 설명. */
        detail: z.string().max(300).nullable().optional(),
    })
        .optional(),
    /**
     * 정비 화면에서 고르는 설정 중 **콘솔에서도 정하는 것**의 지금 값, 그리고 이 기기가 할 수 있는 일.
     *
     * 콘솔은 원하는 값을 정해 두고(`display.agent_settings`) 화면은 받아 적용하지만, 실제로 그렇게
     * 됐는지는 화면만 안다 — 자동 실행은 셸의 설정이고, 재부팅은 기기 권한(루트·기기 소유자)에
     * 달렸다. 콘솔은 이 값으로 "기기는 지금 켜짐", "이 기기는 재부팅 불가" 를 그린다.
     *
     * 0.7.6 부터 보낸다. 없으면 **원격 설정을 모르는 옛 에이전트**다 — 콘솔이 그렇게 말한다.
     * null 은 그 값을 읽을 수 없다는 뜻이다(셸이 없는 브라우저 키오스크 등).
     */
    agent_settings: z
        .object({
        auto_update: z.boolean().nullable(),
        autostart: z.boolean().nullable(),
        reboot: z.boolean().nullable(),
        power_off: z.boolean().nullable(),
        /**
         * 이 에이전트가 알아듣는 원격 명령(0.7.7+). 없으면 재부팅만 아는 0.7.6 이다 —
         * 그 판은 명령 종류를 보지 않고 무엇이든 재부팅으로 처리하므로, 서버는 여기 없는
         * 명령을 걸지 않는다.
         */
        commands: z
            .array(z.string().max(40))
            .max(16)
            .optional()
            // 모르는 이름은 버린다 — 거절하면 이 서버보다 새 화면의 하트비트가 통째로 떨어진다(위 `AGENT_COMMAND_KINDS`).
            .transform((kinds) => kinds?.filter(isAgentCommandKind)),
    })
        .optional(),
    /** 기기 상태 — CPU·메모리·온도·가동 시간·망(0.7.17+). 옛 화면은 보내지 않는다. */
    health: deviceHealthSchema.optional().catch(undefined),
    /**
     * 이 화면이 그릴 줄 아는 콘텐츠 종류(0.7.17+). 서버는 여기 없는 종류를 이 화면의 편성에서 뺀다 — 올린 HTML(`html`)은
     * 샌드박스로 띄울 줄 아는 화면에만 간다(`LEGACY_CONTENT_TYPES`). 처음 다섯 밖의 템플릿(반반 화면)은 `template:split` 처럼
     * 알린 화면에만 간다(2026-10-30). 모르는 이름은 버리고, 모양이 틀리면 그 칸만 버린다.
     */
    content_types: z
        .array(z.string().max(20))
        .max(16)
        .optional()
        .catch(undefined)
        .transform((types) => types?.filter((type) => CONTENT_TYPE_NAMES.includes(type))),
    /**
     * 패널(TV·모니터) 전원 — 운영 시간 밖에 패널을 끄도록 정한 화면(0.7.17+)이 보낸다. `off` 면 쉬는 동안 패널을 꺼
     * 두었다. 끄기를 쓰지 않는 화면은 보내지 않는다. 모르는 값은 그 칸만 버린다.
     */
    panel: z.enum(['on', 'off']).optional().catch(undefined),
    /**
     * 콘솔이 보낸 명령(재부팅·앱 종료·전원 종료)을 받아 어떻게 됐는가. 서버는 이 `id` 를 보고
     * 명령을 거둔다(ack).
     *
     * `started` 는 **명령을 걸기 직전**에 보낸다 — 걸리면 그 뒤로는 보낼 틈이 없다.
     */
    agent_command: z
        .object({
        id: z.string().min(1).max(64),
        kind: z.enum(AGENT_COMMAND_KINDS),
        state: z.enum(['started', 'failed']),
        detail: z.string().max(300).nullable().optional(),
    })
        .optional(),
});
/**
 * 화면이 로컬에 들고 있는 콘텐츠 파일 목록.
 *
 * **화면만 아는 사실이다.** 서버는 무엇을 내려보냈는지는 알아도 그것이 지금도
 * 그 기기에 남아 있는지는 모른다 — 브라우저가 저장 공간이 빠듯해 임의로 버릴 수도,
 * 사람이 앱 데이터를 지웠을 수도 있다. 그래서 운영이 "이 화면은 지금 무엇을
 * 갖고 있나"에 답하려면 화면이 직접 세어 올려야 한다.
 *
 * `evicted` 는 **방금 지운 것**이다. 서버가 내린 삭제 지시를 수행했다는 응답이고,
 * 이것이 곧 ack 다 — 따로 왕복을 두지 않는 이유는 삭제의 증거가 "목록에서 사라진
 * 것" 자체이기 때문이다.
 *
 * 상한 1000개는 본문 상한(1MB)에서 역산한 값이다. 그보다 많이 들고 있는 화면은
 * 앞의 1000개만 보고하고, 총량은 `total_bytes` 로 따로 전한다.
 */
export const cacheInventorySchema = z.object({
    items: z
        .array(z.object({
        /**
         * 캐시에 들어 있는 그대로의 주소.
         *
         * **자산 id 가 아니라 URL 로 주고받는다.** 화면이 캐시에서 아는 것은 URL 뿐이고,
         * 거기서 id 를 뽑으려면 `/api/v1/player/media/:id` 라는 경로 규칙을 화면이 알아야
         * 한다. 그 규칙을 화면에 박아 두면 서버가 경로를 바꾸는 순간 이미 깔린 화면들이
         * 조용히 어긋난다 — 서버가 만든 주소는 서버가 되읽는 것이 맞다.
         */
        url: z.string().min(1).max(2000),
        /** 이 파일이 차지한 바이트. 옛 캐시 항목은 크기를 모를 수 있어 0 이 온다. */
        bytes: z.number().int().min(0).max(64 * 1024 * 1024 * 1024),
        /** 내려받은 시각. 이 정보를 남기기 전에 받은 파일은 null 이다. */
        cached_at: z.coerce.date().nullable().default(null),
    }))
        .max(1000),
    /** 캐시 전체가 차지한 바이트. `items` 가 잘렸어도 이 값은 전체를 말한다. */
    total_bytes: z.number().int().min(0),
    /** 이번에 지운 주소. 서버가 내린 삭제 지시를 수행했다는 뜻이다. */
    evicted: z.array(z.string().min(1).max(2000)).max(1000).default([]),
});
export const playEventsSchema = z.object({
    events: z
        .array(z.object({
        asset_id: objectIdSchema,
        playlist_id: objectIdSchema.nullable().default(null),
        played_at: z.coerce.date(),
        duration_shown_sec: z.number().min(0).max(24 * 3600),
        /** 클라이언트 생성 멱등키 - 재전송이 중복 집계되지 않게 한다. */
        idem_key: z.string().min(8).max(64),
    }))
        .min(1)
        .max(500),
});
//# sourceMappingURL=schemas.js.map